import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getRosterProviders,
  createRosterProvider,
  updateRosterProvider,
  deleteRosterProvider,
  testProviderConnection,
  triggerProviderSync,
  getProviderSyncStatus,
  uploadRosterCsv,
  getRosterProviderById,
} from '../../services/roster.service';
import type {
  RosterProviderWrite,
  SyncTriggerRequest,
} from '../../types/RosterTypes';

export const rosterKeys = {
  all: ['roster'] as const,
  providers: () => [...rosterKeys.all, 'providers'] as const,
  providersById: (id: string) => [...rosterKeys.all, 'providers', id] as const,

  syncStatus: (configId: number) =>
    [...rosterKeys.all, 'sync-status', configId] as const,
};

// =============================================================================
// Queries
// =============================================================================

export function useRosterProviders() {
  return useQuery({
    queryKey: rosterKeys.providers(),
    queryFn: getRosterProviders,
  });
}

export function useSyncStatus(configId: number | null) {
  return useQuery({
    queryKey: rosterKeys.syncStatus(configId as number),
    queryFn: () => getProviderSyncStatus(configId as number),
    enabled: configId !== null,
  });
}
export function useRosterProviderById(id: string, enabled = true) {
  return useQuery({
    queryKey: rosterKeys.providersById(id),
    queryFn: () => getRosterProviderById(id),
    enabled: enabled && !!id,
  });
}
// =============================================================================
// Mutations
// =============================================================================

export function useCreateProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RosterProviderWrite) => createRosterProvider(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rosterKeys.providers() });
    },
  });
}

export function useUpdateProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      configId,
      data,
    }: {
      configId: number;
      data: Partial<RosterProviderWrite>;
    }) => updateRosterProvider(configId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rosterKeys.providers() });
    },
  });
}

export function useDeleteProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (configId: number) => deleteRosterProvider(configId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rosterKeys.providers() });
    },
  });
}

export function useTestConnection() {
  return useMutation({
    mutationFn: (configId: number) => testProviderConnection(configId),
  });
}

export function useTriggerSync() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      configId,
      data,
    }: {
      configId: number;
      data: SyncTriggerRequest;
    }) => triggerProviderSync(configId, data),
    onSuccess: (_result, { configId }) => {
      queryClient.invalidateQueries({
        queryKey: rosterKeys.syncStatus(configId),
      });
    },
  });
}

export function useUploadCsv() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      providerConfigId,
    }: {
      file: File;
      providerConfigId: number;
    }) => uploadRosterCsv(file, providerConfigId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rosterKeys.providers() });
    },
  });
}
